/**
 * @file Submission UI — ContributorDetailsReadonlySection.
 * Contributor Details on Dataset Overview — same stacked field UI as the contributor form.
 */

import React from "react";
import "../../styles/formBase.css";
import "../../pages/Dashboard/Contributor/Stages/ContributorDetails/ContributorDetails.css";
import "./ContributorDetailsReadonlySection.css";
import ContributorDetailsFieldRenderer from "../../pages/Dashboard/Contributor/Stages/ContributorDetails/components/ContributorDetailsFieldRenderer";
import DatasetOverviewFormShell from "./datasetOverview/DatasetOverviewFormShell";
import {
  buildContributorDetailsReadonlyObject,
  displayContributorFieldValue,
  orderedContributorFormFields,
  type ContributorAnyObj,
} from "./contributorDetailsReadonly";
import { stripFieldInfoText } from "./readonlyFormUtils";

const noopChange = () => {};

/** Type definition — Contributor Details Readonly Section Props. */
export type ContributorDetailsReadonlySectionProps = {
  submission: ContributorAnyObj;
};

const ContributorDetailsReadonlySection: React.FC<
  ContributorDetailsReadonlySectionProps
> = ({ submission }) => {
  const formSchema: ContributorAnyObj[] = Array.isArray(submission.formSchema)
    ? submission.formSchema
    : [];

  const data = buildContributorDetailsReadonlyObject(submission);
  const fields = orderedContributorFormFields(formSchema).filter(
    (f) => f.isEnabled !== false
  );

  if (!data) {
    return (
      <DatasetOverviewFormShell
        variant="contributor"
        emptyMessage="No contributor details available."
      />
    );
  }

  if (fields.length === 0) {
    return (
      <DatasetOverviewFormShell
        variant="contributor"
        emptyMessage="No contributor fields configured."
      />
    );
  }

  return (
    <DatasetOverviewFormShell variant="contributor">
      {fields.map((field) => (
        <ContributorDetailsFieldRenderer
          key={field.id || field.name}
          field={stripFieldInfoText(field)}
          value={displayContributorFieldValue(field, data)}
          onChange={noopChange}
          disabled
          isBackgroundOtherEnabled
        />
      ))}
    </DatasetOverviewFormShell>
  );
};

/** React component — Contributor Details Readonly Section. */
export default ContributorDetailsReadonlySection;
